/**
 * Safety net for the youth environment.
 *
 * The primary protection is that youth routes are always reached with a full
 * page load, so GTM / Meta Pixel / Clarity are never injected (see
 * index.html and src/config/youthEnvironment.js). If a youth route is ever
 * reached by client-side navigation while trackers are already in memory,
 * this component neutralises them: consent is set to denied, fbq becomes a
 * no-op, Clarity is stopped, Sentry replay is stopped, and tracker cookies
 * are removed.
 */

import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { isYouthPath } from '@/config/youthEnvironment'
import consentService from '@/services/cookiesPolicy/consentService'
import cookieService from '@/services/cookiesPolicy/cookieService'

const CLARITY_COOKIES = ['_clck', '_clsk', 'CLID', 'ANONCHK', 'MR', 'MUID', 'SM']

function denyGtmConsent() {
  if (!Array.isArray(window.dataLayer)) return
  window.dataLayer.push({
    consent: 'update',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted'
  })
}

function disableMetaPixel() {
  if (typeof window.fbq !== 'function' && typeof window._fbq !== 'function')
    return
  const noop = function () {}
  noop.queue = []
  noop.loaded = true
  noop.disablePushState = true
  window.fbq = noop
  window._fbq = noop
}

function stopClarity() {
  if (typeof window.clarity !== 'function') return
  try {
    window.clarity('stop')
  } catch (_) {}
}

function stopSentryReplay() {
  try {
    const replay = Sentry.getReplay()
    if (replay) replay.stop()
  } catch (_) {}
}

function clearTrackerCookies() {
  try {
    consentService.clearThirdPartyAnalyticsCookies()
    consentService.clearThirdPartyMarketingCookies()
    CLARITY_COOKIES.forEach((name) => cookieService.deleteCookie(name))
  } catch (_) {}
}

export function neutraliseTrackersForYouth() {
  if (typeof window === 'undefined') return
  window.__digipalzYouthEnv = true
  denyGtmConsent()
  disableMetaPixel()
  stopClarity()
  stopSentryReplay()
  clearTrackerCookies()
}

const YouthEnvironmentGuard = () => {
  const { pathname } = useLocation()
  const appliedRef = useRef(false)

  useEffect(() => {
    if (!isYouthPath(pathname)) {
      appliedRef.current = false
      return
    }
    if (appliedRef.current) return
    appliedRef.current = true
    neutraliseTrackersForYouth()
  }, [pathname])

  return null
}

export default YouthEnvironmentGuard
